import React, {useState, useCallback, useMemo} from 'react';
import {useMutation} from '@tanstack/react-query';
import {useAPI} from '../../config/API';
import {useAuth} from '../../providers/AuthProvider';
import LoadingBanner from '../../components/LoadingBanner';
import ErrorBanner from '../../components/ErrorBanner';
import {Button} from "antd";

const TAB_LABELS = {
    0: "Client-Side",
    1: "Agency-Side",
    2: "Talent-Side",
    3: "Admin-Side",
};

const COLUMN_LABELS = {
    Personal: "Personal",
    "Client Organization": "Across Organization",
};

const getGroupColumns = (group) => {
    const typeSet = new Set();
    group.settings.forEach((s) =>
        s.options.forEach((o) => typeSet.add(o.settings_type))
    );
    const order = ["Personal", "Client Organization"];
    return order.filter((t) => typeSet.has(t));
};

/* ═══════════════════════════════════════════════════════
   Toggle Switch
   ═══════════════════════════════════════════════════════ */
const Toggle = ({checked, indeterminate, onChange, onClick}) => {
    return (
        <label
            className="relative inline-block shrink-0 cursor-pointer"
            style={{width: 40, height: 22}}
            onClick={onClick}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                className="opacity-0 w-0 h-0 absolute"
            />
            <span
                className="absolute inset-0 rounded-full transition-colors duration-200"
                style={{
                    backgroundColor: indeterminate
                        ? "rgba(139,82,255,0.5)"
                        : checked
                            ? "#6b2cf2"
                            : "#d6d8e3",
                }}
            >
                <span
                    className="absolute bg-white rounded-full transition-transform duration-200"
                    style={{
                        width: 16,
                        height: 16,
                        top: 3,
                        left: 3,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                        transform: indeterminate
                            ? "translateX(9px)"
                            : checked
                                ? "translateX(18px)"
                                : "translateX(0)",
                    }}
                />
            </span>
        </label>
    );
};

/* ═══════════════════════════════════════════════════════
   Chevron
   ═══════════════════════════════════════════════════════ */
const ChevronIcon = ({open}) => {
    return (
        <svg
            className="transition-transform duration-200"
            style={{
                width: 12,
                height: 12,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
};

/* ═══════════════════════════════════════════════════════
   Group
   ═══════════════════════════════════════════════════════ */
const Group = ({group, formModel, onToggleProp, onToggleMaster}) => {
    const [open, setOpen] = useState(false);
    const columns = useMemo(() => getGroupColumns(group), [group]);

    const allProps = useMemo(
        () => group.settings.flatMap((s) => s.options.map((o) => o.settings_prop)),
        [group]
    );

    const checkedCount = allProps.filter((p) => formModel[p]).length;
    const total = allProps.length;
    const allOn = checkedCount === total;
    const someOn = checkedCount > 0 && checkedCount < total;
    const isCustom = someOn;

    const COL_W = 80;


    return (
        <div
            className="rounded-lg bg-white overflow-hidden transition-all duration-150"
            style={{
                border: open ? "1px solid #d4d7e1" : "1px solid #e5e7ee",
                boxShadow: open ? "0 1px 2px rgba(20,20,40,0.04)" : "none",
            }}
        >
            {/* Header row */}
            <div
                className="flex items-center gap-6 cursor-pointer select-none"
                style={{padding: "18px 22px"}}
                onClick={(e) => {
                    if (e.target.closest("label")) return;
                    setOpen((o) => !o);
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafd")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-0.5">
                        <span className="font-semibold" style={{fontSize: "15.5px", color: "#1a1a2e"}}>
                            {group.name}
                        </span>
                        
                        {isCustom && (
                            <span
                                className="font-semibold uppercase rounded-full"
                                style={{
                                    fontSize: "10.5px",
                                    letterSpacing: "0.5px",
                                    background: "#f3edff",
                                    color: "#6b2cf2",
                                    padding: "3px 8px",
                                }}
                            >
                                Custom
                            </span>
                        )}
                    </div>
                    <div style={{fontSize: 13, color: "#6b7088"}}>{group.description}</div>
                </div>

                <span className="text-xs">{checkedCount}/{total} Active</span>

                <Toggle
                    checked={allOn || someOn}
                    indeterminate={someOn}
                    onChange={() => onToggleMaster(allProps, !(allOn || someOn))}
                    onClick={(e) => e.stopPropagation()}
                />

                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 bg-transparent rounded-md cursor-pointer transition-all duration-150 shrink-0"
                    style={{
                        border: "1px solid #e5e7ee",
                        color: "#6b7088",
                        fontSize: "12.5px",
                        fontWeight: 500,
                        padding: "7px 12px",
                        fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#6b2cf2";
                        e.currentTarget.style.color = "#6b2cf2";
                        e.currentTarget.style.background = "#f3edff";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7ee";
                        e.currentTarget.style.color = "#6b7088";
                        e.currentTarget.style.background = "transparent";
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen((o) => !o);
                    }}
                >
                    <span>{open ? "Hide" : "Customize"}</span>
                    <ChevronIcon open={open}/>
                </button>
            </div>

            {/* Expanded panel */}
            <div
                className="overflow-hidden transition-all duration-300"
                style={{
                    maxHeight: open ? 3000 : 0,
                    borderTop: open ? "1px solid #e5e7ee" : "1px solid transparent",
                }}
            >
                <div style={{padding: "18px 22px 22px", background: "#fbfbfd"}}>
                    <div
                        className="font-semibold uppercase"
                        style={{fontSize: 11, letterSpacing: "1.2px", color: "#9ea3b5", marginBottom: 12}}
                    >
                        Individual Notifications
                    </div>

                    {/* Column headers */}
                    <div
                        className="flex items-center"
                        style={{padding: "0 12px 8px", borderBottom: "1px solid #e5e7ee"}}
                    >
                        <div className="flex-1"/>
                        {columns.map((col) => (
                            <div
                                key={col}
                                className="text-center font-medium shrink-0"
                                style={{width: COL_W, fontSize: 11, color: "#6b7088"}}
                            >
                                {COLUMN_LABELS[col] || col}
                            </div>
                        ))}
                    </div>

                    {/* Settings rows */}
                    {group.settings
                        .slice()
                        .sort((a, b) => a.display_index - b.display_index)
                        .map((setting, i) => {
                            const isLast = i === group.settings.length - 1;
                            return (
                                <div
                                    key={setting.id}
                                    className="flex items-center"
                                    style={{
                                        padding: "14px 12px",
                                        borderBottom: isLast ? "none" : "1px solid #e5e7ee",
                                    }}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium"
                                             style={{fontSize: 14, color: "#1a1a2e", marginBottom: 2}}>
                                            {setting.name}
                                        </div>
                                        <div style={{fontSize: "12.5px", color: "#6b7088"}}>{setting.description}</div>
                                    </div>
                                    {columns.map((colType) => {
                                        const opt = setting.options.find((o) => o.settings_type === colType);
                                        if (!opt) return <div key={colType} className="shrink-0" style={{width: COL_W}}/>;
                                        return (
                                            <div key={colType} className="flex justify-center shrink-0"
                                                 style={{width: COL_W}}>
                                                <Toggle
                                                    checked={!!formModel[opt.settings_prop]}
                                                    onChange={() => onToggleProp(opt.settings_prop)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════
   Page
   ═══════════════════════════════════════════════════════ */
const NewNotificationSettings = () => {
    const {auth} = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [formModel, setFormModel] = useState(auth?.settings || laravel.auth_notification_settings || {});

    const {
        data: currentNotificationSettings,
        isFetching,
        isRefetching,
        error,
        refetch
    } = useAPI(['fetchUserNotificationSettings', {}], {
        enabled: true,
        onSuccess: async (response) => {
            console.log('onSuccess', response)
            await setFormModel(auth?.settings || laravel.auth_notification_settings);
        },
        onError: (e) => {
            console.warn('ERROR: onError', e);
        }
    });

    const toggleProp = useCallback((prop) => {
        setFormModel((prev) => ({...prev, [prop]: !prev[prop]}));
    }, []);

    const toggleMaster = useCallback((props, value) => {
        setFormModel((prev) => {
            const next = {...prev};
            props.forEach((p) => (next[p] = value));
            return next;
        });
    }, []);

    const tabs = currentNotificationSettings || [];

    const activeTabData = useMemo(
        () => tabs.find((t) => t.tab_type === activeTab) || TB.first(tabs || []),
        [tabs, activeTab]
    );

    const disableAll = useCallback(() => {
        if (!activeTabData) return;
        setFormModel((prev) => {
            const next = {...prev};
            activeTabData.groups.forEach((group) =>
                group.settings.forEach((s) =>
                    s.options.forEach((o) => (next[o.settings_prop] = false))
                )
            );
            return next;
        });
    }, [activeTabData]);

    const isAllDisabled = useMemo(() => {
        if (!activeTabData) return;
        let isEnabled = false;
        TB.map(activeTabData.groups, (group) =>
            TB.map(group.settings, setting => {
                TB.map(setting.options, (option) => {
                    if (formModel[option.settings_prop]) {
                        // console.log('lckamcdslksm', option)
                        isEnabled = true;
                    }
                })
            })
        );

        return !isEnabled
    }, [activeTabData, Object.values(formModel)]);

    const saveMutation = useMutation(
        ['saveNotificationSettings'],
        async (params) => {
            const response = await TB.request(
                TB.api_endpoint('/api/notification-settings/update'), params
            ).post();

            if (!TB.has(response, 'success')) {
                throw new Error('Something went wrong. Please try again.');
            }

            return response;
        },
        {
            onSuccess: async () => {
                await TB.alert(
                    'success',
                    'auth.edit_settings.success.title',
                    'auth.edit_settings.success.message'
                );
            },
            onError: (e) => {
                console.warn('saveNotificationSettings error', e);
                TB.toast(e?.message || 'Something went wrong. Please try again.', {theme: 'danger'});
            },
        }
    );

    const handleSave = useCallback(() => {
        saveMutation.mutate({...formModel});
    }, [saveMutation]);

    return (
        <div
            style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                background: "#f4f5f9",
                color: "#1a1a2e",
                lineHeight: 1.5,
                minHeight: "100vh",
                padding: "32px 16px 80px",
                WebkitFontSmoothing: "antialiased",
            }}
        >
            {/* Top accent bar */}
            <div
                className="mx-auto bg-black"
                style={{maxWidth: 920, height: 10, borderRadius: "6px 6px 0 0"}}
            />

            {/* Page card */}
            <div
                className="mx-auto bg-white"
                style={{
                    maxWidth: 920,
                    borderRadius: "0 0 12px 12px",
                    boxShadow: "0 4px 16px rgba(20,20,40,0.06)",
                    padding: "40px 48px 32px",
                }}
            >
                <div
                    className="font-semibold uppercase"
                    style={{color: "#6b2cf2", fontSize: 12, letterSpacing: 2, marginBottom: 14}}
                >
                    Notification Email Preferences
                </div>

                <h1
                    className="font-bold"
                    style={{fontSize: 28, letterSpacing: "-0.02em", marginBottom: 10}}
                >
                    Notification Email Preferences
                </h1>

                <p style={{color: "#6b7088", fontSize: "14.5px", maxWidth: 620, marginBottom: 28}}>
                    Choose which email updates you want to receive. Start with
                    category-level settings, or expand a category to customize individual
                    notifications.
                </p>

                {isFetching && <LoadingBanner/>}

                {error && (
                    <ErrorBanner
                        {...(TB.isObject(error) ? error : {title: 'Something went wrong.'})}
                        onRetry={refetch}
                    />
                )}

                {!isFetching && !error && tabs.length > 0 && (
                    <>
                        {/* Tabs */}
                        {tabs?.length > 1 && (
                            <div
                                className="flex"
                                style={{borderBottom: "2px solid #e5e7ee", marginBottom: 24}}
                            >
                                { tabs.map((tab) => {
                                    const isActive = activeTab === tab.tab_type;
                                    return (
                                        <button
                                            key={tab.tab_type}
                                            type="button"
                                            className="cursor-pointer transition-colors duration-150"
                                            style={{
                                                padding: "10px 20px",
                                                fontSize: "14px",
                                                fontWeight: isActive ? 600 : 500,
                                                color: isActive ? "#6b2cf2" : "#6b7088",
                                                background: "transparent",
                                                border: "none",
                                                borderBottom: isActive ? "2px solid #6b2cf2" : "2px solid transparent",
                                                marginBottom: -2,
                                                fontFamily: "inherit",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isActive) e.currentTarget.style.color = "#1a1a2e";
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive) e.currentTarget.style.color = "#6b7088";
                                            }}
                                            onClick={() => setActiveTab(tab.tab_type)}
                                        >
                                            {TAB_LABELS[tab.tab_type] || `Tab ${tab.tab_type}`}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Groups for active tab */}
                        {activeTabData && (
                            <div className="flex flex-col gap-3">
                                {activeTabData.groups
                                    .slice()
                                    .sort((a, b) => a.display_index - b.display_index)
                                    .map((group) => (
                                        <Group
                                            key={`${activeTab}-${group.display_index}`}
                                            group={group}
                                            formModel={formModel}
                                            onToggleProp={toggleProp}
                                            onToggleMaster={toggleMaster}
                                        />
                                    ))}
                            </div>
                        )}

                        {/* Actions */}
                        {activeTabData && (
                            <div
                                className="flex justify-between items-center"
                                style={{marginTop: 40, paddingTop: 24, borderTop: "1px solid #e5e7ee"}}
                            >
                                <button
                                    onClick={async () => {
                                        await disableAll();
                                        return handleSave()
                                    }}
                                    disabled={isFetching || saveMutation?.isLoading || isAllDisabled}
                                    className="btn btn-dark text-sm"
                                >
                                    Disable All
                                </button>
                                <Button
                                    onClick={() => handleSave()}
                                    disabled={isFetching || saveMutation?.isLoading}
                                    type="primary"
                                    loading={saveMutation.isLoading}
                                >
                                    {saveMutation.isLoading ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default NewNotificationSettings;